// src/pages/Chat/MessageBubble.jsx
// Phase 7a–7d: reactions, edit, delete, reply-to — all wired up.
//
// Props:
//   message       — message object from store
//   isOwn         — bool: is this the current user's message?
//   showAvatar    — bool: show avatar (first in a run)
//   currentUserId — employee_id of the logged-in user
//   onReply       — (message) => void  — set reply-to in parent input
//   onEdit        — (message) => void  — trigger inline edit in parent input
//   onDelete      — (message) => void  — trigger delete confirm
//   onReact       — (messageId, emoji) => void
//   onRemoveReact — (messageId, emoji) => void

import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FiEdit2, FiTrash2, FiSmile, FiCornerUpRight, FiFile, FiDownload, FiImage, FiVideo, FiMusic, FiCheckCircle, FiEye } from "react-icons/fi";
import { ChatCrypto } from "../../utils/crypto";
import useChatStore, { EMPTY_ARRAY } from "../../store/chatStore";
import { resolvePhotoUrl } from "../../utils/photoHelper";
import PollDetailsModal from "./PollDetailsModal";
import apiClient from "../../api/apiClient";
import { sendMessage } from "../../api/chatService";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function getInitials(name = "") {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";
}

const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🎉"];

// ── Reaction bar ──────────────────────────────────────────────────────────────

function ReactionBar({ reactions = [], currentUserId, onReact, onRemoveReact, messageId }) {
  // reactions from server: array of { emoji, count, reactors: [employee_id, ...], reactor_names: [...] }
  // or raw: array of { emoji, user: { employee_id, full_name } }
  const grouped = React.useMemo(() => {
    if (!reactions.length) return [];
    // If already grouped (has .count)
    if (reactions[0]?.count !== undefined) return reactions;
    // Raw array — group manually
    const map = {};
    reactions.forEach(({ emoji, user }) => {
      if (!map[emoji]) map[emoji] = { emoji, count: 0, reactors: [], reactor_names: [] };
      map[emoji].count += 1;
      map[emoji].reactors.push(user?.employee_id || "");
      map[emoji].reactor_names.push(user?.full_name || user?.employee_id || "Someone");
    });
    return Object.values(map);
  }, [reactions]);

  if (!grouped.length) return null;

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {grouped.map(({ emoji, count, reactors, reactor_names }) => {
        const iReacted = reactors?.includes(currentUserId);
        // Build tooltip with reactor names
        const names = (reactor_names || []).slice(0, 5).join(", ");
        const extra = (reactor_names || []).length > 5 ? ` +${reactor_names.length - 5} more` : "";
        const tooltip = names + extra;
        return (
          <button
            key={emoji}
            onClick={() =>
              iReacted ? onRemoveReact?.(messageId, emoji) : onReact?.(messageId, emoji)
            }
            className={`
              inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full
              border transition-all duration-150
              ${
                iReacted
                  ? "bg-blue-600/30 border-blue-500/60 text-blue-200"
                  : "bg-white/8 border-white/10 text-slate-300 hover:bg-white/15 hover:border-white/20"
              }
            `}
            title={tooltip || (iReacted ? "Remove reaction" : "Add reaction")}
          >
            <span>{emoji}</span>
            {count > 1 && <span className="text-[10px] opacity-80">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Emoji popup ───────────────────────────────────────────────────────────────

function EmojiPopup({ onSelect, onClose, isOwn }) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className={`
        absolute bottom-full mb-1 z-50
        bg-slate-800 border border-white/10 rounded-xl shadow-2xl
        p-1.5 flex gap-0.5
        ${isOwn ? "right-0" : "left-0"}
      `}
    >
      {QUICK_EMOJIS.map((emoji) => (
        <button
          key={emoji}
          onClick={() => {
            onSelect(emoji);
            onClose();
          }}
          className="text-lg p-1 rounded-lg hover:bg-white/10 transition-colors leading-none"
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

function Poll({ message, currentUserId, aesKey, isOwn }) {
  const [showDetails, setShowDetails] = useState(false);
  const [optimisticVote, setOptimisticVote] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);

  const messages = useChatStore((s) => s.messages[message.conversation] ?? EMPTY_ARRAY);
  const globalSend = useChatStore((s) => s.sendWebSocketMessage); 

  const pollData = React.useMemo(() => {
    const raw = message.decryptedContent || message.content;
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return { ...parsed, conversation: message.conversation };
    } catch (e) {
      return null;
    }
  }, [message.decryptedContent, message.content, message.conversation]);

  const votes = React.useMemo(() => {
    const v = {};
    messages.forEach((m) => {
      if (m.msg_type === "POLL_VOTE") {
        const rawVote = m.decryptedContent || m.content;
        if (rawVote) {
          try {
            const vote = JSON.parse(rawVote);
            if (vote.poll_id === pollData?.poll_id) {
              v[m.sender_id || m.sender?.employee_id] = vote.option_index;
            }
          } catch (e) {}
        }
      }
    });
    return v;
  }, [messages, pollData?.poll_id]);

  const myVoteFromStore = votes[currentUserId];
  const effectiveMyVote = optimisticVote !== undefined ? optimisticVote : myVoteFromStore;

  // Clear optimistic vote once store syncs
  React.useEffect(() => {
    if (optimisticVote !== undefined && myVoteFromStore === optimisticVote) {
      setOptimisticVote(undefined);
    }
  }, [myVoteFromStore, optimisticVote]);

  const counts = React.useMemo(() => {
    const c = (pollData?.options || []).map(() => 0);
    Object.entries(votes).forEach(([voterId, optIdx]) => {
      // Exclude current user's store vote if we have an optimistic override
      if (voterId === currentUserId && optimisticVote !== undefined) return;
      if (optIdx !== null && c[optIdx] !== undefined) c[optIdx]++;
    });
    if (effectiveMyVote !== null && effectiveMyVote !== undefined && c[effectiveMyVote] !== undefined) {
      c[effectiveMyVote]++;
    }
    return c;
  }, [votes, pollData?.options, currentUserId, optimisticVote, effectiveMyVote]);

  const totalVotes = counts.reduce((sum, n) => sum + n, 0);

  const handleVote = async (index) => {
    if (submitting) return;
    const isDeselect = effectiveMyVote === index;
    const nextVote = isDeselect ? null : index;
    
    setOptimisticVote(nextVote);
    setSubmitting(true);

    const votePayload = {
      poll_id: pollData.poll_id,
      option_index: nextVote,
      is_vote: true
    };

    const strPayload = JSON.stringify(votePayload);
    const clientMsgId = crypto.randomUUID();

    const payload = {
      type: "send_message",
      conv_id: message.conversation,
      content: strPayload,
      ciphertext: "",
      iv: "",
      signature: "",
      msg_type: "POLL_VOTE",
      client_msg_id: clientMsgId,
    };

    if (aesKey) {
      const { ciphertext, iv } = await ChatCrypto.encrypt(strPayload, aesKey);
      payload.ciphertext = ciphertext;
      payload.iv = iv;
    }

    try {
      const isWsConnected = useChatStore.getState().wsConnected;
      if (globalSend && isWsConnected) {
        globalSend(payload);
      } else {
        await sendMessage(message.conversation, payload);
      }
    } catch (err) {
      console.warn("[Poll] WS send failed, trying HTTP REST fallback:", err);
      try {
        await sendMessage(message.conversation, payload);
      } catch (restErr) {
        console.error("[Poll] Vote submission failed:", restErr);
        // Revert optimistic vote on complete failure
        setOptimisticVote(undefined);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!pollData) return <div className="text-xs italic opacity-50">Invalid poll data</div>;

  return (
    <div className="flex flex-col min-w-[260px]">
      <div className="p-3.5 space-y-4">
        <h4 className="text-[15px] font-bold text-white leading-tight">{pollData.question}</h4>
        
        <div className="flex items-center gap-2 text-[#8696a0] select-none">
          <FiCheckCircle size={14} className="opacity-70" />
          <span className="text-[12px]">Select one or more</span>
        </div>

        <div className="space-y-4">
          {pollData.options.map((option, idx) => {
            const count = counts[idx] || 0;
            const pct = totalVotes > 0 ? (count / totalVotes) * 100 : 0;
            const selected = effectiveMyVote === idx;

            return (
              <div key={idx} className="space-y-1.5">
                <button
                  onClick={() => handleVote(idx)}
                  className="w-full text-left flex items-center justify-between gap-3 group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${selected ? "border-[#00a884] bg-[#00a884]" : "border-[#8696a0]"}`}>
                      {selected && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <span className={`text-[14px] truncate ${selected ? "text-white font-medium" : "text-slate-200"}`}>
                      {option}
                    </span>
                  </div>
                  <span className="text-[13px] text-[#8696a0] font-medium shrink-0">
                    {count}
                  </span>
                </button>
                <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#00a884] transition-all duration-500 rounded-full" 
                    style={{ width: `${pct}%` }} 
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-1.5 pt-1">
          <span className="text-[10px] text-[#8696a0]">
            {formatTime(message.created_at)}
          </span>
          {isOwn && (
             <div className="flex items-center ml-0.5">
               {message.is_read ? (
                 <span className="text-yellow-400 flex items-center" style={{ fontSize: '14px', lineHeight: 1 }}>
                   <span style={{ marginRight: '-7px' }}>✓</span>
                   <span>✓</span>
                 </span>
               ) : message.is_delivered ? (
                 <span className="text-blue-200/50 flex items-center" style={{ fontSize: '14px', lineHeight: 1 }}>
                   <span style={{ marginRight: '-7px' }}>✓</span>
                   <span>✓</span>
                 </span>
               ) : (
                 <span className="text-blue-200/50" style={{ fontSize: '14px', lineHeight: 1 }}>
                   ✓
                 </span>
               )}
             </div>
          )}
        </div>
      </div>

      <button 
        onClick={() => setShowDetails(true)}
        className="w-full py-2.5 border-t border-white/5 text-[14px] font-semibold text-[#53bdeb] hover:bg-white/5 transition-colors"
      >
        View votes
      </button>

      {showDetails && createPortal(
        <PollDetailsModal 
          pollData={pollData} 
          votes={votes} 
          messages={messages}
          pollSenderId={message.sender_id || message.sender?.employee_id}
          onClose={() => setShowDetails(false)} 
        />,
        document.body
      )}
    </div>
  );
}

// ── Attachment Item ──────────────────────────────────────────────────────────

function AttachmentItem({ attachment, aesKey }) {
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [downloadError, setDownloadError] = useState(false);
  const isAudio = attachment.mime_category === "audio";
  const isEncrypted = !!attachment.encrypted_file_key;

  const filename = attachment.original_filename || "";
  const isPreviewable = (() => {
    const ext = filename.split(".").pop().toLowerCase();
    const previewableExtensions = [
      "jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico",
      "mp4", "webm", "ogg", "mov",
      "mp3", "wav", "m4a", "aac", "oga",
      "pdf",
      "txt", "csv", "json", "md", "xml", "html", "css", "js", "ts", "py", "sh"
    ];
    return previewableExtensions.includes(ext);
  })();

  const downloadUrl = React.useMemo(() => {
    const url = attachment.download_url || "";
    if (url.includes("/chat/api/files/")) {
      return "/chat/api/files/" + url.split("/chat/api/files/")[1];
    }
    return url;
  }, [attachment.download_url]);

  const handleDownload = useCallback(async (isManual = false) => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(false);
    try {
      const response = await apiClient.get(downloadUrl, {
        responseType: "arraybuffer",
      });

      let blob;
      if (isEncrypted && aesKey) {
        const encryptedBuffer = response.data;
        const decryptedBuffer = await ChatCrypto.decryptFile(
          encryptedBuffer,
          attachment.encrypted_file_key,
          attachment.file_key_iv,
          attachment.file_iv,
          aesKey
        );
        blob = new Blob([decryptedBuffer], { type: isAudio ? "audio/webm" : "application/octet-stream" });
      } else {
        blob = new Blob([response.data], { type: isAudio ? "audio/webm" : "application/octet-stream" });
      }

      const url = URL.createObjectURL(blob);
      
      if (isAudio) {
        setAudioUrl(url);
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = attachment.original_filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("[Attachment] Decryption/Download failed:", err);
      setDownloadError(true);
      if (isManual === true && !isAudio) {
        alert("Failed to download file.");
      }
    } finally {
      setDownloading(false);
    }
  }, [downloadUrl, attachment, aesKey, isAudio, isEncrypted, downloading]);

  useEffect(() => {
    if (isAudio && !audioUrl && !downloading && !downloadError) {
      // If E2EE is active, wait for the aesKey to be derived first
      if (isEncrypted && !aesKey) {
        return;
      }
      handleDownload(false);
    }
  }, [isAudio, audioUrl, downloading, downloadError, isEncrypted, aesKey, handleDownload]);

  const handlePreview = async (e) => {
    e.stopPropagation();
    if (previewing) return;
    setPreviewing(true);
    try {
      const response = await apiClient.get(downloadUrl, {
        responseType: "arraybuffer",
      });

      let blob;
      let mimeType = "application/octet-stream";
      const filenameLower = attachment.original_filename?.toLowerCase() || "";
      if (attachment.mime_category === "image") {
        mimeType = "image/png";
      } else if (attachment.mime_category === "video") {
        mimeType = "video/mp4";
      } else if (filenameLower.endsWith(".pdf")) {
        mimeType = "application/pdf";
      } else if (filenameLower.endsWith(".txt")) {
        mimeType = "text/plain";
      }

      if (attachment.encrypted_file_key && aesKey) {
        const encryptedBuffer = response.data;
        const decryptedBuffer = await ChatCrypto.decryptFile(
          encryptedBuffer,
          attachment.encrypted_file_key,
          attachment.file_key_iv,
          attachment.file_iv,
          aesKey
        );
        blob = new Blob([decryptedBuffer], { type: mimeType });
      } else {
        blob = new Blob([response.data], { type: mimeType });
      }

      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      console.error("[Attachment] Preview failed:", err);
      alert("Failed to preview file.");
    } finally {
      setPreviewing(false);
    }
  };

  const handleCardClick = (e) => {
    if (!isPreviewable) {
      e.preventDefault();
      handleDownload(true);
    }
  };

  const getIcon = () => {
    switch (attachment.mime_category) {
      case "image":    return <FiImage className="text-blue-400" size={18} />;
      case "video":    return <FiVideo className="text-amber-400" size={18} />;
      case "audio":    return <FiMusic className="text-emerald-400" size={18} />;
      case "document": return <FiFile className="text-indigo-400" size={18} />;
      default:         return <FiFile className="text-slate-400" size={18} />;
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  if (isAudio && audioUrl) {
    return (
      <div className="mt-2 min-w-[240px]">
        <audio controls src={audioUrl} className="w-full h-8 custom-audio" />
      </div>
    );
  }

  return (
    <div
      onClick={handleCardClick}
      className={`group/file flex items-center gap-3 p-2 rounded-xl bg-white/5 border border-white/10 mt-2 min-w-[200px] max-w-full relative ${
        !isPreviewable ? "cursor-pointer hover:bg-white/10 transition-colors" : ""
      }`}
    >
      <div className="shrink-0 relative w-8 h-8 flex items-center justify-center bg-white/5 rounded-lg overflow-hidden">
        {/* Normal file icon */}
        <div className={`transition-opacity duration-200 ${isPreviewable ? "group-hover/file:opacity-0" : ""} w-full h-full flex items-center justify-center`}>
          {getIcon()}
        </div>

        {/* Eye icon overlay on hover (only if previewable) */}
        {isPreviewable && (
          <button
            onClick={handlePreview}
            disabled={previewing}
            className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover/file:opacity-100 transition-opacity duration-200 text-blue-400 hover:text-blue-300"
            title="Preview in new tab"
          >
            {previewing ? (
              <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin text-white" />
            ) : (
              <FiEye size={16} />
            )}
          </button>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-slate-200 truncate" title={attachment.original_filename}>
          {attachment.original_filename}
        </div>
        <div className="text-[10px] text-slate-500">
          {downloadError ? <span className="text-red-400">Download failed</span> : formatSize(attachment.file_size)}
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDownload(true);
        }}
        disabled={downloading}
        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all shrink-0"
        title={isAudio ? "Play Voice Note" : "Download and Decrypt"}
      >
        {downloading ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          isAudio ? <FiMusic size={14} /> : <FiDownload size={14} />
        )}
      </button>
    </div>
  );
}

// ── Message Avatar Helper ───────────────────────────────────────────────────

function MessageAvatar({ photoUrl, name, onClick }) {
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    setImgErr(false);
  }, [photoUrl]);

  const initials = getInitials(name);

  return (
    <button 
      onClick={onClick}
      className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center text-white text-[10px] font-bold select-none overflow-hidden hover:scale-110 transition-transform"
    >
      {photoUrl && !imgErr ? (
        <img
          src={photoUrl}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => setImgErr(true)}
        />
      ) : (
        initials
      )}
    </button>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function MessageBubble({
  message,
  isOwn,
  showAvatar = true,
  currentUserId,
  isConvAdmin = false,
  onReply,
  onEdit,
  onDelete,
  onReact,
  onRemoveReact,
  onViewProfile,
}) {
  const [hovered,    setHovered]    = useState(false);
  const [showEmoji,  setShowEmoji]  = useState(false);
  const aesKey = useChatStore((s) => s.conversationKeys[message.conversation]);

  if (!message) return null;

  // ── Deleted message placeholder ───────────────────────────────────────────
  if (message.is_deleted) {
    return (
      <div
        className={`flex items-end gap-2 px-4 py-0.5 ${isOwn ? "flex-row-reverse" : ""}`}
      >
        <div className="w-7 h-7 shrink-0" />
        <div className="italic text-xs text-slate-500 px-3 py-2 rounded-2xl bg-white/5 border border-white/8">
          This message was deleted.
        </div>
      </div>
    );
  }

  // ── Resolve sender display info ───────────────────────────────────────────
  const sender      = message.sender || {};
  // Prefer sender_name snapshot; fall back to nested user object fields
  const senderName  =
    message.sender_name ||
    sender.full_name ||
    (sender.first_name ? `${sender.first_name} ${sender.last_name || ""}`.trim() : null) ||
    sender.employee_id ||
    "Unknown";

  const time       = formatTime(message.created_at);
  const rawPhoto =
    sender.profile_photo ||
    sender.photo ||
    sender.user?.profile_photo ||
    sender.user?.photo ||
    sender.user_info?.profile_photo ||
    sender.user_info?.photo ||
    sender.avatar ||
    sender.user?.avatar ||
    sender.user_info?.avatar ||
    null;
  const photoUrl   = resolvePhotoUrl(rawPhoto);

  // ── System / Vote messages ───────────────────────────────────────────────────
  if (message.msg_type === "SYSTEM") {
    return (
      <div className="flex justify-center py-1 px-4">
        <span className="text-[11px] text-slate-500 bg-white/5 border border-white/8 rounded-full px-3 py-0.5">
          {message.content}
        </span>
      </div>
    );
  }

  if (message.msg_type === "POLL_VOTE") return null; // Votes are aggregated, not shown as bubbles

  // ── Resolve what text to show ───────────────────────────────────────────────
  const isEncrypted = !!message.ciphertext;
  
  const bodyText = message.decryptedContent || (message.content && message.content.trim() ? message.content : null);

  const isSticker = message.msg_type === "STICKER" || (bodyText && (bodyText.startsWith("data:image/") || bodyText.includes("giphy.com")));

  return (
    <div
      className={`group flex items-end gap-2 px-4 py-0.5 ${isOwn ? "flex-row-reverse" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setShowEmoji(false); }}
    >
      {/* ── Avatar ── */}
      <div className="w-7 h-7 shrink-0 self-end">
        {showAvatar && !isOwn && (
          <MessageAvatar
            photoUrl={photoUrl}
            name={senderName}
            onClick={() => onViewProfile?.(sender)}
          />
        )}
      </div>

      {/* ── Bubble column ── */}
      <div className={`flex flex-col max-w-[70%] relative ${isOwn ? "items-end" : "items-start"}`}>

        {/* Sender name — only for others, only first in run */}
        {!isOwn && showAvatar && (
          <span 
            onClick={() => onViewProfile?.(sender)}
            className="text-[11px] font-medium text-slate-400 mb-0.5 ml-1 select-none cursor-pointer hover:text-blue-400 transition-colors"
          >
            {senderName}
          </span>
        )}

        {/* Reply preview */}
        {message.reply_to && !message.reply_to.is_deleted && (
          <div
            className={`
              text-[11px] text-slate-400 mb-1 px-2.5 py-1.5 rounded-xl
              border-l-2 border-blue-400/60 bg-white/5 max-w-full
              ${isOwn ? "self-end border-l-0 border-r-2 border-r-blue-400/60" : "self-start"}
            `}
          >
            <span className="font-semibold text-slate-300 block">
              {message.reply_to.sender_name || "Unknown"}
            </span>
            <span className="truncate block max-w-xs">
              {message.reply_to.content || "[encrypted message]"}
            </span>
          </div>
        )}

        {/* Main bubble */}
        <div
          className={`
            relative text-sm leading-relaxed break-words whitespace-pre-wrap overflow-hidden
            ${message.msg_type === "POLL" ? "rounded-2xl" : isSticker ? "p-0 rounded-xl" : "px-3.5 py-2.5 rounded-2xl"}
            ${
              isOwn
                ? (message.msg_type === "POLL" ? "bg-blue-600/90 rounded-br-sm" : isSticker ? "bg-transparent text-white" : "bg-blue-600/90 rounded-br-sm") + " text-white"
                : (message.msg_type === "POLL" ? "bg-white/10 backdrop-blur-sm rounded-bl-sm border border-white/10" : isSticker ? "bg-transparent text-slate-100" : "bg-white/10 backdrop-blur-sm rounded-bl-sm border border-white/10") + " text-slate-100"
            }
          `}
        >


          {message.msg_type === "POLL" ? (
            <Poll message={message} currentUserId={currentUserId} aesKey={aesKey} isOwn={isOwn} />
          ) : isSticker ? (
            <div className="relative group/sticker">
              <img 
                src={bodyText} 
                alt="Sticker" 
                className="max-w-[150px] max-h-[150px] object-contain rounded-xl select-none animate-in zoom-in duration-200" 
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          ) : (
            bodyText ? bodyText : (isEncrypted ? (
              <span className="italic text-opacity-60 text-[13px]" style={{ opacity: 0.55 }}>
                [decrypting...]
              </span>
            ) : null)
          )}

          {/* Attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="flex flex-col gap-1 mt-1">
              {message.attachments.map((at) => (
                <AttachmentItem key={at.id} attachment={at} aesKey={aesKey} />
              ))}
            </div>
          )}

          {/* Timestamp row */}
          {message.msg_type !== "POLL" && (
            <div
              className={`flex items-center gap-1.5 mt-0.5 ${
                isOwn ? "justify-end" : "justify-start"
              }`}
            >
            {message.is_edited && (
              <span className={`text-[10px] ${isOwn ? "text-blue-200/60" : "text-slate-500"}`}>
                edited
              </span>
            )}
            <span className={`text-[10px] ${isOwn ? "text-blue-200/70" : "text-slate-500"}`}>
              {time}
            </span>
            {/* Issue 12: Read receipt ticks — only shown on own messages */}
            {isOwn && (
              <div className="flex items-center ml-0.5">
                {message.is_read ? (
                  <span className="text-yellow-400 flex items-center" style={{ fontSize: '14px', lineHeight: 1 }}>
                    <span style={{ marginRight: '-7px' }}>✓</span>
                    <span>✓</span>
                  </span>
                ) : message.is_delivered ? (
                  <span className="text-blue-200/50 flex items-center" style={{ fontSize: '14px', lineHeight: 1 }}>
                    <span style={{ marginRight: '-7px' }}>✓</span>
                    <span>✓</span>
                  </span>
                ) : (
                  <span className="text-blue-200/50" style={{ fontSize: '14px', lineHeight: 1 }}>
                    ✓
                  </span>
                )}
              </div>
            )}
          </div>
        )}
        </div>

        {/* Reactions */}
        <ReactionBar
          reactions={message.reactions || []}
          currentUserId={currentUserId}
          onReact={onReact}
          onRemoveReact={onRemoveReact}
          messageId={message.id}
        />
      </div>

      {/* ── Hover action bar ── */}
      <div
        className={`
          relative flex items-center gap-0.5 self-center
          transition-all duration-150
          ${hovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1 pointer-events-none"}
        `}
      >
        {/* Emoji quick-react */}
        <div className="relative">
          <ActionBtn
            icon={<FiSmile size={13} />}
            title="React"
            onClick={() => setShowEmoji((v) => !v)}
          />
          {showEmoji && (
            <EmojiPopup
              isOwn={isOwn}
              onSelect={(emoji) => onReact?.(message.id, emoji)}
              onClose={() => setShowEmoji(false)}
            />
          )}
        </div>

        <ActionBtn
          icon={<FiCornerUpRight size={13} />}
          title="Reply"
          onClick={() => onReply?.(message)}
        />

        {isOwn && (
          <ActionBtn
            icon={<FiEdit2 size={13} />}
            title="Edit"
            onClick={() => onEdit?.(message)}
          />
        )}

        {(isOwn || isConvAdmin) && (
          <ActionBtn
            icon={<FiTrash2 size={13} />}
            title="Delete"
            danger
            onClick={() => onDelete?.(message)}
          />
        )}
      </div>
    </div>
  );
}

function ActionBtn({ icon, title, danger = false, onClick }) {
  return (
    <button
      title={title}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className={`
        p-1.5 rounded-lg transition-colors
        ${
          danger
            ? "text-red-400 hover:bg-red-500/20 hover:text-red-300"
            : "text-slate-400 hover:bg-white/10 hover:text-slate-200"
        }
      `}
    >
      {icon}
    </button>
  );
}